import { yupResolver } from '@hookform/resolvers/yup';
import { useStytch, useStytchUser } from '@stytch/nextjs';
import axios from 'axios';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { Resolver, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import Button from 'components/app-components/ui/button';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useUser } from 'context/user';
import Warning from 'icons/Warning';
import { isMFARejectedCodeError, OTP_EXPIRATION_MINUTES } from 'lib/constants';
import { sleep } from 'lib/miscellaneous';
import { SESSION_DURATION_MINUTES } from 'pages/authenticate';
import { AuthRoutes } from 'routes';
import { updateMFAUserDetails } from 'services/companyApi';
import { MFAMethodType, TOTPSetupData } from 'types/mfa';
import { formatErrorMessage, logError } from 'utils/error';
import OTPInput from '../form/OTPInput';
import GenericModal from '../modals/GenericModal';
import TwoFACodes from './2FACodes';

interface ValidateOTPModalProps {
  open: boolean;
  onClose: (isLoggingIn?: boolean) => void;
  onSuccess?: () => void;
  methodID?: string;
  email?: string;
  selectedMethod?: string;
  totpData?: TOTPSetupData | null;
  isDarkMode?: boolean;
  isSignInValidationModal?: boolean;
  onDeviceStolen?: () => void;
  saveUserMFA?: boolean;
  userPhoneNumber?: string;
  onUnableToReceiveCode?: () => void;
  isSendingEmailOTP?: boolean;
  onTOTPRegistrationGone?: () => void;
}

interface TwoFactorAuthForm {
  code: string[]; // An array of strings for each digit of the OTP
}

// Stytch reports "this registration is gone" and "this code does not match" as different types,
// and during setup the two need opposite advice: one means start over, the other means the
// authenticator app is still holding a secret from an earlier attempt.
const TOTP_REGISTRATION_GONE_ERRORS = ['totps_not_found_for_user', 'totp_not_found', 'expired_totp'];
const TOTP_CODE_MISMATCH_ERROR = 'unable_to_authenticate_totp';
// Stytch folds "already used" and "expired" into this single type, so the advice has to cover
// both: get a fresh code rather than retype the one on screen.
const OTP_CODE_REJECTED_ERROR = 'unable_to_auth_otp_code';

const resolveMFAErrorMessage = (errorType: string, isEnrolling: boolean): string | undefined => {
  if (TOTP_REGISTRATION_GONE_ERRORS.includes(errorType)) {
    return isEnrolling
      ? 'This setup has expired. Close this and start the 2FA setup again.'
      : 'The code is not valid or expired. Please try log in again.';
  }

  if (isEnrolling && errorType === TOTP_CODE_MISMATCH_ERROR) {
    return 'That code did not match. Remove any older Thera entries from your authenticator app, scan the QR code above again, then enter the new 6-digit code.';
  }

  if (errorType === OTP_CODE_REJECTED_ERROR) {
    return 'That code has already been used or has expired. Send a new code and enter the latest one.';
  }

  return undefined;
};

const schema = yup
  .object({
    code: yup
      .array()
      .of(
        yup
          .string()
          .required('A digit is required')
          .matches(/^[0-9]$/, 'Each input must be a single digit'),
      )
      .length(6, 'The code must be exactly 6 digits long'),
  })
  .required();

export default function ValidateOTPModal({
  open,
  onClose,
  onSuccess,
  methodID,
  email,
  selectedMethod,
  totpData,
  isDarkMode,
  isSignInValidationModal,
  onDeviceStolen,
  saveUserMFA,
  userPhoneNumber,
  onUnableToReceiveCode,
  isSendingEmailOTP,
  onTOTPRegistrationGone,
}: ValidateOTPModalProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<TwoFactorAuthForm>({
    resolver: yupResolver(schema) as Resolver<TwoFactorAuthForm>,
    mode: 'onBlur',
    defaultValues: {
      code: ['', '', '', '', '', ''],
    },
  });
  const router = useRouter();
  const stytchClient = useStytch();
  const { user: stytchUser } = useStytchUser();
  const { user, mutate: mutateUser } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  // Saving the recovery codes is advisory, so this starts true and TwoFACodes only ever confirms
  // it. The guard in onSubmit stays inert unless we decide to gate Verify on saving them.
  const [hasUserSavedCodes, setHasUserSavedCodes] = useState(true);
  const showDeviceStolenBtn = selectedMethod === 'TOTP' && isSignInValidationModal;
  // Enrolling means a QR code is on screen and the registration is not confirmed yet, which
  // changes both the copy on failure and whether a 401 should end the session.
  const isTOTPEnrollment = selectedMethod === MFAMethodType.TOTP && !isSignInValidationModal;
  const totpId = stytchUser?.totps?.[0]?.totp_id;
  const [_hasRetriedSendingPhoneOTP, setHasRetriedSendingPhoneOTP] = useState(false);
  const hasUserSetUp2FA = Boolean(user?.mfaUserDetails?.mfaType);

  // Memoized submit handler
  const onSubmit = useCallback<SubmitHandler<TwoFactorAuthForm>>(
    async (data) => {
      if (!hasUserSavedCodes) {
        theraToast.warning('Please save the codes before verifying.', {
          id: 'save-codes-before-verifying',
        });
        return;
      }
      setIsSubmitting(true);
      try {
        const fullCode = data.code.join('');

        switch (selectedMethod) {
          case MFAMethodType.EMAIL:
          case MFAMethodType.SMS:
          case MFAMethodType.WHATSAPP:
            await stytchClient.otps.authenticate(fullCode, methodID, {
              session_duration_minutes: SESSION_DURATION_MINUTES,
            });
            break;
          case MFAMethodType.TOTP:
            await stytchClient.totps.authenticate({
              totp_code: fullCode,
              session_duration_minutes: SESSION_DURATION_MINUTES,
            });
            break;
        }
        if (saveUserMFA) {
          await updateMFAUserDetails(user?.userID, {
            mfaUserDetails: {
              mfaType: selectedMethod.toUpperCase(),
            },
          });
          // delete the existing TOTP if it exists for non TOTP methods
          if (totpId && selectedMethod !== MFAMethodType.TOTP) {
            await axios
              .delete('/api/mfa/delete-totp', {
                data: {
                  totpId: totpId,
                },
              })
              .catch((err) => {
                logError(err, { source: 'FailedToDeleteTOTP', data: { totpId, user } });
              });
          }
        }

        await sleep(1500); // hacky fix to ensure the user is updated after 2FA
        // we need to refresh session here so that proper session gets updated after 2FA
        await mutateUser({
          shouldRefreshSession: true,
        });
        theraToast.success(saveUserMFA ? '2FA setup complete.' : '2FA verified. You are securely logged in.');
        onSuccess?.();
        onClose(true);
      } catch (error) {
        const errorMsg = error?.body;
        const httpStatusCode = error?.status_code;
        const defaultErrorMessage = 'OTP verification failed.\nPlease check your code and try again.';
        // Stytch rejects a wrong code with a 401, for the authenticator app and for the
        // SMS/WhatsApp/email codes alike. Neither is the same thing as a dead session and
        // neither may be treated as one.
        const isRejectedCode = isMFARejectedCodeError(error?.error_type);
        const toastMessage =
          httpStatusCode === 401 && !isRejectedCode
            ? 'Your session has expired. Please login again.'
            : httpStatusCode === 429
              ? 'Too many requests. Please try again later in some time or contact Thera Support support@getthera.com'
              : resolveMFAErrorMessage(error?.error_type, isTOTPEnrollment) ||
                formatErrorMessage(errorMsg, defaultErrorMessage);
        theraToast.error({
          message: toastMessage,
          error: error,
          options: {
            id: 'otp-verification-failed',
            sendToSlack: false,
            data: {
              user,
              _2FAMethod: selectedMethod,
            },
          },
        });
        // Bouncing to sign-in on a rejected code strands the user: re-entering setup issues a
        // fresh secret, which invalidates the QR code they just scanned, so every later attempt
        // fails the same way and the loop never breaks.
        if (httpStatusCode === 401 && !isRejectedCode) {
          setTimeout(() => {
            router.replace(AuthRoutes.SignIn);
          }, 1000);
        }
        if (isTOTPEnrollment && TOTP_REGISTRATION_GONE_ERRORS.includes(error?.error_type)) {
          onTOTPRegistrationGone?.();
        }
        if (selectedMethod === MFAMethodType.TOTP) {
          setHasRetriedSendingPhoneOTP(true);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasUserSavedCodes,
      selectedMethod,
      isTOTPEnrollment,
      onTOTPRegistrationGone,
      saveUserMFA,
      onSuccess,
      onClose,
      mutateUser,
      stytchClient,
      methodID,
      totpId,
      user,
    ],
  );

  // Memoize resend handler
  const handleResendClick = useCallback(async () => {
    if (selectedMethod === MFAMethodType.TOTP) return;

    // Reset the form
    Array.from({ length: 6 }).forEach((_, index) => {
      setValue(`code.${index}`, '');
    });

    setIsResending(true);
    try {
      switch (selectedMethod) {
        case MFAMethodType.EMAIL:
          await stytchClient.otps.email.send(email, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
          break;
        case MFAMethodType.SMS:
          await stytchClient.otps.sms.send(userPhoneNumber, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
          setHasRetriedSendingPhoneOTP(true);
          break;
        case MFAMethodType.WHATSAPP:
          await stytchClient.otps.whatsapp.send(userPhoneNumber, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
          setHasRetriedSendingPhoneOTP(true);
          break;
      }

      const methodLabel =
        selectedMethod === MFAMethodType.EMAIL
          ? 'email'
          : selectedMethod === MFAMethodType.WHATSAPP
            ? 'WhatsApp'
            : 'phone number';
      theraToast.success(`OTP sent to ${methodLabel}.`, {
        id: 'otp-sent',
      });
    } catch (error) {
      const httpStatusCode = error?.status_code;
      const toastMessage =
        httpStatusCode === 429
          ? 'Too many requests. Please try again later in some time or Contact Support.'
          : 'Failed to resend the code';
      theraToast.error({
        message: toastMessage,
        error: error,
        options: {
          id: 'otp-resend-failed',
        },
      });
    } finally {
      setIsResending(false);
    }
  }, [selectedMethod, setValue, stytchClient, email, userPhoneNumber]);

  const handleLostDevice = async () => {
    onDeviceStolen?.();
  };

  const handleUnableToReceiveCode = async () => {
    onUnableToReceiveCode?.();
  };

  const titleText = !isSignInValidationModal
    ? `Set up using ${selectedMethod === MFAMethodType.EMAIL ? 'email' : selectedMethod === MFAMethodType.SMS ? 'SMS' : selectedMethod === MFAMethodType.WHATSAPP ? 'WhatsApp' : 'Authenticator app'}`
    : 'Enter your 2FA code';

  const subTitleText =
    !isSignInValidationModal && selectedMethod === MFAMethodType.TOTP
      ? ''
      : selectedMethod === MFAMethodType.EMAIL
        ? "We've sent the code to your email address"
        : selectedMethod === MFAMethodType.SMS
          ? "We've sent the code to your phone number"
          : selectedMethod === MFAMethodType.WHATSAPP
            ? "We've sent the code to your WhatsApp number"
            : 'Enter the code from your authenticator app';

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      title={titleText}
      subTitle={subTitleText}
      width="w-[30rem]"
      titleCenter
      showHeaderBorder={false}
      isProcessing={isResending || isSubmitting}
      dialogClass="relative z-50"
      isDarkMode={isDarkMode}
    >
      {totpData && <TwoFACodes totpData={totpData} setHasUserSavedCodes={setHasUserSavedCodes} />}
      <form onSubmit={handleSubmit(onSubmit)} className="form">
        {isTOTPEnrollment && (
          <p className="border-x px-4 pt-3 text-base font-medium text-neutral-900">Enter the 6-digit code</p>
        )}
        <OTPInput
          rhfControl={control}
          containerClassName={isTOTPEnrollment ? 'rounded-b-xl border border-t-0 p-4' : ''}
          isDarkMode={isDarkMode}
          errors={errors}
          onSubmit={handleSubmit(onSubmit)}
          rhfSetValue={setValue}
        />
        {totpData && (
          <p className="mt-2 text-sm font-medium text-neutral-500">
            <Warning className="mr-2 inline-block" strokeColor="#737373" />
            Please make sure you have saved the recovery codes.
          </p>
        )}
        <Button
          htmlType="submit"
          className="mt-4 w-full"
          variant="primary"
          loading={isSubmitting}
          aria-label="Verify OTP code"
        >
          Verify
        </Button>

        <div className="mt-2 flex items-center justify-center gap-3">
          {selectedMethod !== MFAMethodType.TOTP && (
            <Button
              variant="plain"
              onClick={handleResendClick}
              className={`text-sm font-medium text-neutral-500 underline`}
              loading={isResending}
              aria-label="Resend verification code"
              isDarkMode={isDarkMode}
            >
              Resend Code
            </Button>
          )}
          <Button
            variant="plain"
            onClick={handleUnableToReceiveCode}
            className={`text-sm font-medium text-neutral-500 underline ${hasUserSetUp2FA ? '' : 'hidden'}`}
            loading={isSendingEmailOTP}
            aria-label="Unable to receive code?"
            isDarkMode={isDarkMode}
          >
            Unable to receive code?
          </Button>
        </div>
        {showDeviceStolenBtn && (
          <div className="mt-2 text-right">
            <Button
              variant="plain"
              onClick={handleLostDevice}
              className="mt-2 w-full text-sm font-medium text-neutral-500 underline"
              disabled={isSubmitting}
            >
              Lost your device?
            </Button>
          </div>
        )}
      </form>
    </GenericModal>
  );
}
